// src/components/ProjectDeliverablesTable.js
import React, { useMemo, useState, useEffect } from "react";
import Papa from "papaparse";
import "../styles/anthology.css";

const AUDIT_UPDATED = "Last Updated";
const AUDIT_BY = "Last Updated By";

// New: Exceptions column name
const EXCEPTIONS_COL = "Exceptions";

// 🔒 Never load/show/store these fields anywhere
const HIDDEN_FIELDS = ["Target Date", "Matched", "Milestone - Aug", "Milestone - Sep", "In End Game", "Release"];

/** Fields that should get date highlighting (past = red, next 7 days = yellow) */
const DEADLINE_FIELDS = [
    "Discovery Target",
    "Reqs Target",
    "Dev Target",
    "UAT Target",
    "Prod Target",
    "C/T Target",
    "Data Target",
    "Support Target",
];

// NEW: ADO “system” columns we’ll insert next to ADO
const ADO_SYS_COLS = [
    "System.AreaPath",
    "System.State",
    "System.IterationPath",
    "System.AssignedTo.displayName",
    "System.Tags",
];

const DEFAULT_VISIBLE = [
    "Rank",
    "Current Sprint",
    "Tracker",
    "SOW",
    "TPM",
    "Customer",
    "Cloud Type",
    "Go-live",
    "Urgency",
    "Item Rank",
    "Item",
    "Item Status",
    "Type",
    "T-Shirt Size",
    "ADO",
    "Team",
    "SCRUM",
    "Discovery Target",
    "Reqs Target",
    "Dev Target",
    "UAT Target",
    "Prod Target",
    "Prod Accepted",
    "C/T Target",
    "Total Hrs",
    // audit columns visible by default
    AUDIT_UPDATED,
    AUDIT_BY,
];

// Default form-field order from your provided header row (+ audit fields appended)
// ✅ Updated: "Deploy Type" ➜ "Cloud Type"
const FORM_FIELD_ORDER = [
    "Rank","Customer","Cloud Type","TPM","Go-live","Urgency","Item Rank","Item","Item Status","Type","ADO","Release","Driver",
    "Team","SCRUM","Discovery Target","Reqs Target","Dev Target","UAT Target","Prod Target",
    "Prod Accepted","Support Target","C/T Target","EA","BA","BA2","TC","TC2","Dev","Dev2","QA",
    "Total Hrs","Arch Hrs","Reqs Hrs","Dev Hrs","Test Hrs","In End Game","Tracker","Notes",
    AUDIT_UPDATED, AUDIT_BY
];

const sortCycle = (dir) => (dir === "asc" ? "desc" : dir === "desc" ? null : "asc");

const asType = (v) => {
    if (v === null || v === undefined) return { t: "empty", v: "" };
    const s = String(v).trim();
    if (s === "") return { t: "empty", v: "" };
    const n = Number(s.replace(/[, ]/g, ""));
    if (!Number.isNaN(n) && s.match(/^-?\d+(\.\d+)?(e[+-]?\d+)?$/i)) return { t: "num", v: n };
    const d = Date.parse(s);
    if (!Number.isNaN(d)) return { t: "date", v: d };
    return { t: "str", v: s.toLowerCase() };
};

const cmp = (a, b) => {
    const A = asType(a), B = asType(b);
    if (A.t === "empty" && B.t !== "empty") return 1;
    if (B.t === "empty" && A.t !== "empty") return -1;
    if (A.t === B.t) return A.v < B.v ? -1 : A.v > B.v ? 1 : 0;
    return String(a).localeCompare(String(b));
};

/** ---------- Rank helpers (numeric) ---------- **/
function coerceRankNumber(v) {
    if (typeof v === "number" && Number.isFinite(v)) return v;
    const s = String(v ?? "").trim();
    if (!s) return null;
    const sanitized = s.replace(/[, ]/g, "");
    const n = Number(sanitized);
    return Number.isFinite(n) ? n : null;
}
function rankSortValue(v) {
    const n = coerceRankNumber(v);
    return n === null ? Number.POSITIVE_INFINITY : n;
}

/** ---------- Re-Rank helpers ---------- **/
const startsWith979899 = (d) => /^\s*(97|98|99)/.test(String(d ?? "").trim());
const parseDateOrInf = (s) => {
    const t = Date.parse(String(s ?? "").trim());
    return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY; // invalid/blank dates sort last
};
// numeric sort value for "Item Rank"
const itemRankSortValue = (v) => {
    const n = coerceRankNumber(v);
    return n === null ? Number.POSITIVE_INFINITY : n;
};

/** ---------- Filter parser ---------- **/
const parseFilter = (raw) => {
    const s = String(raw ?? "").trim();
    if (!s) return null;
    if (/^(!|NOT\s+)/i.test(s)) {
        const needle = s.replace(/^(!|NOT\s+)/i, "").toLowerCase();
        return { mode: "not", needle };
    }
    return { mode: "contains", needle: s.toLowerCase() };
};

// Local date-time formatter: "YYYY-MM-DD HH:mm"
function formatDateTimeLocal(d = new Date()) {
    const pad = (n) => String(n).padStart(2, "0");
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const min = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
}

/** ---------- Date highlighting helpers ---------- **/
function toMidnight(dateLike) {
    const d = new Date(dateLike);
    d.setHours(0, 0, 0, 0);
    return d;
}
function getDateHighlight(value) {
    const raw = String(value ?? "").trim();
    if (!raw) return { style: null, title: null };
    const t = Date.parse(raw);
    if (!Number.isNaN(t)) {
        // Only highlight when the value parses like a date (not "DONE")
        const today = toMidnight(new Date());
        const dt = toMidnight(t);

        const diffMs = dt - today;
        const oneDay = 24 * 60 * 60 * 1000;

        if (dt < today) {
            // Past due -> red
            return {
                style: {
                    background: "rgba(220,20,60,0.15)",
                    color: "crimson",
                    fontWeight: 600,
                    padding: "0 4px",
                    borderRadius: 4,
                },
                title: "Past due",
            };
        }
        if (diffMs >= 0 && diffMs <= 7 * oneDay) {
            // Due within a week -> yellow
            return {
                style: {
                    background: "rgba(255,215,0,0.2)",
                    color: "#9c6f00",
                    fontWeight: 600,
                    padding: "0 4px",
                    borderRadius: 4,
                },
                title: "Due within 7 days",
            };
        }
    }
    return { style: null, title: null };
}

/** ---------- Assignee initials helper ---------- **/
function getInitials(name) {
    if (!name) return "";
    const parts = String(name).trim().split(/\s+/);
    if (parts.length === 1) return (parts[0][0] || "").toUpperCase();
    const first = parts[0][0] || "";
    const last = parts[parts.length - 1][0] || "";
    return (first + last).toUpperCase();
}

/** ---------- Header label helper (UI-only rename) ---------- **/
function displayHeader(col) {
    if (col === "System.AssignedTo.displayName") return "ADO Assignee";
    if (col === "System.AreaPath") return "ADO Customer";
    if (col === "System.State") return "ADO State";
    if (col === "System.IterationPath") return "ADO Iteration";
    if (col === "System.Tags") return "ADO Tags";
    return col;
}

/** ---------- Exception indicator helpers ---------- **/
// Build the messages list for a row
function getExceptionMessages(row) {
    const msgs = [];

    // Helpers
    const isDone = (v) => String(v ?? "").trim().toUpperCase() === "DONE";
    const adoIteration = String(row["System.IterationPath"] ?? "").trim();
    const typeNorm = String(row["Type"] ?? "").trim().toLowerCase();
    const isBugFix = typeNorm === "bug fix" || typeNorm === "bug-fix" || typeNorm === "bugfix";
    const isTrainingOrConsulting = typeNorm === "training" || typeNorm === "consulting";
    const teamERP = String(row["Team"] ?? "").trim().toUpperCase() === "ERP";

    // Rule 1: ADO Iteration contains "Blocker"
    if (/\bblocker\b/i.test(adoIteration)) {
        msgs.push("Deliverable is in a Blocker State");
    }

    // Rule 2: Missing TPM or SCRUM Team
    const tpmBlank = String(row["TPM"] ?? "").trim() === "";
    const scrumBlank = String(row["SCRUM"] ?? "").trim() === "";
    if (tpmBlank || scrumBlank) {
        msgs.push("Missing TPM or Team Assignment");
    }

    // Rule 3: Prod DONE but Support not DONE
    if (isDone(row["Prod Target"]) && !isDone(row["Support Target"])) {
        msgs.push("Deliverable is DONE in Prod but not transitioned to PS Support");
    }

    // Suppress UAT/Prod exceptions for Bug Fix AND Training/Consulting
    if (!isBugFix && !isTrainingOrConsulting) {
        // Rule 4: UAT Target is blank or in the past
        const uatRaw = String(row["UAT Target"] ?? "").trim();
        if (!uatRaw) {
            msgs.push("UAT Target is missing or in the past");
        } else if (uatRaw.toUpperCase() !== "DONE") {
            const ts = Date.parse(uatRaw);
            if (Number.isNaN(ts) || toMidnight(ts) < toMidnight(new Date())) {
                msgs.push("UAT Target is missing or in the past");
            }
        }

        // Rule 5: Prod Target is blank or in the past
        const prodRaw = String(row["Prod Target"] ?? "").trim();
        if (!prodRaw) {
            msgs.push("Prod Target is missing or in the past");
        } else if (prodRaw.toUpperCase() !== "DONE") {
            const pts = Date.parse(prodRaw);
            if (Number.isNaN(pts) || toMidnight(pts) < toMidnight(new Date())) {
                msgs.push("Prod Target is missing or in the past");
            }
        }
    }

    // Rule 6: Go-live is blank (still suppressed for Bug Fix only)
    if (!isBugFix) {
        const goLiveRaw = String(row["Go-live"] ?? "").trim();
        if (!goLiveRaw) {
            msgs.push("Go Live date is not set");
        }
    }

    // NEW Rule: Team is ERP and ADO is blank (or "N/A")
    const adoId = String(row["ADO"] ?? "").trim();
    const adoBlank = !adoId || adoId.toLowerCase() === "n/a";
    if (teamERP && adoBlank) {
        msgs.push("ADO ticket is blank");
    }

    // Rule 7: High priority but not in current sprint (Team must be ERP)
    // Conditions: Current Sprint ≠ "Yes" AND Rank < 50 AND Prod Target ≠ DONE AND ADO Iteration NOT "Blocker" AND Team = ERP
    const sprintVal = String(row["Current Sprint"] ?? "").trim().toLowerCase();
    const inCurrentSprint = sprintVal === "yes" || sprintVal === "y" || sprintVal === "true" || sprintVal === "1";
    const rankNum = coerceRankNumber(row["Rank"]);
    const notBlocked = !/\bblocker\b/i.test(adoIteration);

    if (
        teamERP &&
        !inCurrentSprint &&
        rankNum !== null && rankNum < 50 &&
        !isDone(row["Prod Target"]) &&
        notBlocked
    ) {
        msgs.push("Deliverable is a high priority but not in the current sprint");
    }

    // NEW Rule 8: For Training/Consulting, C/T Target blank or in the past
    if (isTrainingOrConsulting) {
        const ctRaw = String(row["C/T Target"] ?? "").trim();
        if (!ctRaw) {
            msgs.push("C/T Target is missing or in the past");
        } else if (ctRaw.toUpperCase() !== "DONE") {
            const cts = Date.parse(ctRaw);
            if (Number.isNaN(cts) || toMidnight(cts) < toMidnight(new Date())) {
                msgs.push("C/T Target is missing or in the past");
            }
        }
    }

    return msgs;
}

// String form used for sorting/filtering/CSV
function getExceptionsString(row) {
    return getExceptionMessages(row).join(" | ");
}

const ProjectDeliverablesTable = ({
                                      defaultVisibleColumns = DEFAULT_VISIBLE,
                                      backEndUrl = "",
                                      platformUrl = "",
                                      role = "TPM", // "TPM" | "BA" | "TC" | "ADMIN"
                                      formFieldOrder = FORM_FIELD_ORDER,
                                      adoProxyUrl = backEndUrl,
                                      sprintCalendarUrl = "sprint_calendar_wide_big.png"
                                  }) => {
    const [rows, setRows] = useState([]);       // includes __state meta
    const [headers, setHeaders] = useState([]); // data headers only (no __state)
    const [visibleCols, setVisibleCols] = useState(defaultVisibleColumns);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [info, setInfo] = useState("");

    const [sortBy, setSortBy] = useState(null);
    const [filters, setFilters] = useState({});

    const [formOpen, setFormOpen] = useState(false);
    const [formMode, setFormMode] = useState("add"); // "add" | "edit"

    const [editIndex, setEditIndex] = useState(null);
    const [formData, setFormData] = useState({});
    const [showSprint, setShowSprint] = useState(false);

    // NEW: whether CSV exports should include only displayed (visible) fields
    const [includeOnlyDisplayed, setIncludeOnlyDisplayed] = useState(false);

    // NEW: ADO cache & busy tracker
    const [adoCache, setAdoCache] = useState({}); // { [id]: { "System.AreaPath": "...", ... } }
    const [adoBusyIds, setAdoBusyIds] = useState(new Set());

    // --- Read uuid & token from URL query string ---
    const queryParams =
        typeof window !== "undefined"
            ? new URLSearchParams(window.location.search)
            : new URLSearchParams();
    const userUuid = queryParams.get("uuid");
    const accessToken = queryParams.get("token");

    // --- Blackboard user + initials (used for default filter by role and audit stamp) ---
    const [userInfo, setUserInfo] = useState(null);
    const [userInitials, setUserInitials] = useState("");
    const [tpmFilterApplied, setTpmFilterApplied] = useState(false); // (applies to TPM/BA/TC once; never for ADMIN)

    useEffect(() => {
        if (!userUuid || !platformUrl || !accessToken) return; // require token

        (async () => {
            try {
                const url = `${platformUrl.replace(/\/+$/, "")}/learn/api/public/v1/users/uuid:${encodeURIComponent(userUuid)}`;
                const res = await fetch(url, {
                    headers: {
                        Accept: "application/json",
                        Authorization: `Bearer ${accessToken}` // Pass token
                    }
                });
                if (!res.ok) throw new Error(`Blackboard user fetch failed: ${res.status}`);
                const json = await res.json();
                setUserInfo(json);

                const initials = [
                    json?.name?.given?.[0] ?? "",
                    json?.name?.family?.[0] ?? "",
                ].join("").toUpperCase();
                setUserInitials(initials);
            } catch (err) {
                console.error("Error fetching Blackboard user info:", err);
            }
        })();
    }, [userUuid, accessToken, platformUrl]);

    // small helper to show transient messages
    const flash = (msg) => {
        setInfo(msg);
        // setTimeout(() => setInfo(""), 6000);
    };

    // Normalize preferred order from array or CSV string
    const normalizeOrder = (order) => {
        if (!order) return [];
        if (Array.isArray(order)) return order.map((s) => String(s).trim()).filter(Boolean);
        return String(order).split(",").map((s) => s.trim()).filter(Boolean);
    };

    // Insert columns immediately after a given anchor column
    const insertColumnsNextTo = (list, anchor, cols) => {
        const next = [...list];
        const anchorIdx = next.indexOf(anchor);
        const missing = cols.filter((c) => !next.includes(c));
        if (missing.length === 0) return next;
        if (anchorIdx === -1) return [...next, ...missing]; // if ADO not found, append to end
        const before = next.slice(0, anchorIdx + 1);
        const after = next.slice(anchorIdx + 1);
        return [...before, ...missing, ...after];
    };

    // The exact order the form should use (intersection + any remaining headers) — hidden fields are never included
    const orderedFormFields = useMemo(() => {
        if (!headers.length) return [];
        const preferred = normalizeOrder(formFieldOrder).filter((h) => !HIDDEN_FIELDS.includes(h));
        const presentPreferred = preferred.filter((h) => headers.includes(h));
        const remaining = headers.filter((h) => !presentPreferred.includes(h));
        return [...presentPreferred, ...remaining];
    }, [headers, formFieldOrder]);

    // --- shared row/header loader (CSV & Cloud) ---
    // initialState: "clean" (cloud) | "updated" (csv)
    const loadItemsIntoState = (items, initialState = "clean") => {
        const raw = Array.isArray(items) ? items : [];

        // Strip hidden fields per-row and add audit fields
        const data = raw.map((r) => {
            const next = Object.fromEntries(
                Object.entries(r || {}).filter(([k]) => !HIDDEN_FIELDS.includes(k))
            );
            next.__state = initialState;

            // Coerce Rank
            if ("Rank" in next) {
                const nr = coerceRankNumber(next.Rank);
                if (nr !== null) next.Rank = nr;
            }
            // Ensure audit fields exist on each row
            if (!(AUDIT_UPDATED in next)) next[AUDIT_UPDATED] = "";
            if (!(AUDIT_BY in next)) next[AUDIT_BY] = "";

            // Keep original Item for rename detection on save
            next.__origItem = next.Item ?? "";

            return next;
        });

        // Build header set (excluding client meta + hidden)
        const colSet = new Set();
        data.forEach((r) =>
            Object.keys(r || {}).forEach((k) => {
                if (k !== "__state" && k !== "__origItem" && !HIDDEN_FIELDS.includes(k)) colSet.add(k);
            })
        );

        // Ensure audit columns exist even if dataset is empty
        colSet.add(AUDIT_UPDATED);
        colSet.add(AUDIT_BY);

        let cols = Array.from(colSet);

        // If we have ADO values and a proxy configured, ensure ADO system columns exist next to ADO
        const hasAnyAdo = data.some((r) => String(r.ADO ?? "").trim());
        if (hasAnyAdo && cols.includes("ADO")) {
            cols = insertColumnsNextTo(cols, "ADO", ADO_SYS_COLS);
        }

        // Ensure Exceptions column exists and is first
        if (!cols.includes(EXCEPTIONS_COL)) {
            cols = [EXCEPTIONS_COL, ...cols];
        }

        // Order: defaults first (only if present), then remaining alphabetically
        const priorityOrder = (defaultVisibleColumns || []).filter((c) => !HIDDEN_FIELDS.includes(c));
        const defaultFirst = priorityOrder.filter((c) => cols.includes(c));

        // Make sure ADO system columns are visible right after ADO if present
        let defaultVisibleWithAdo = [...defaultFirst];
        if (cols.includes("ADO")) {
            defaultVisibleWithAdo = insertColumnsNextTo(
                defaultVisibleWithAdo,
                "ADO",
                ADO_SYS_COLS.filter((c) => cols.includes(c))
            );
        }

        // Make Exceptions visible and left-most
        if (!defaultVisibleWithAdo.includes(EXCEPTIONS_COL)) {
            defaultVisibleWithAdo = [EXCEPTIONS_COL, ...defaultVisibleWithAdo];
        }

        const remaining = cols.filter((c) => !defaultVisibleWithAdo.includes(c)).sort();
        cols = [...defaultVisibleWithAdo, ...remaining];

        setRows(data);
        setHeaders(cols);
        setVisibleCols(defaultVisibleWithAdo);

        // Default sort preference remains Rank if present; user can change later
        setSortBy(cols.includes("Rank") ? { col: "Rank", dir: "asc" } : null);

        // ✅ Role-based default filter (ADMIN => none)
        const colToFilter =
            role === "BA" ? "BA" :
                role === "TPM" ? "TPM" :
                    role === "TC" ? "TC" :
                        null; // ADMIN or any other => no default filter

        const wantDefault = !!userInitials && !!colToFilter && cols.includes(colToFilter);

        // Ensure BA or TC column is visible when role requires it
        if ((role === "BA" || role === "TC") && cols.includes(role) && !defaultVisibleWithAdo.includes(role)) {
            setVisibleCols((prev) => (prev.includes(role) ? prev : [...prev, role]));
        }

        setFilters(wantDefault ? { [colToFilter]: userInitials } : {}); // ADMIN -> {}
        setTpmFilterApplied(!!wantDefault);
        setFormOpen(false);
    };

    // If initials arrive *after* data, apply default filter once (don’t override user changes)
    useEffect(() => {
        // Never auto-apply for ADMIN
        if (role === "ADMIN") return;

        if (!userInitials || tpmFilterApplied === true) return;
        if (!headers.length) return;

        const colToFilter = role === "BA" ? "BA" : role === "TC" ? "TC" : "TPM";
        if (!headers.includes(colToFilter)) return;

        // Ensure BA or TC column is visible for their roles
        if ((role === "BA" || role === "TC") && !visibleCols.includes(role)) {
            setVisibleCols((prev) => [...prev, role]);
        }

        setFilters((f) => ({ ...f, [colToFilter]: userInitials }));
        setTpmFilterApplied(true);
    }, [userInitials, headers, filters, tpmFilterApplied, role, visibleCols]);

    const parseCsv = (text) => {
        setLoading(true);
        setError(null);
        Papa.parse(text, {
            header: true,
            skipEmptyLines: true,
            transformHeader: (h) => h.replace(/\s+/g, " ").trim(),
            complete: (res) => {
                // Filter out rows where every value is blank/whitespace
                const data = (res.data || []).filter(row =>
                    Object.values(row).some(val => String(val ?? "").trim() !== "")
                );
                loadItemsIntoState(data, "updated"); // CSV -> mark all as updated
                setLoading(false);
                flash(`Loaded ${data.length} ${data.length === 1 ? "row" : "rows"} from CSV.`);
            },
            error: (err) => {
                setError(`Failed to parse CSV: ${err.message}`);
                setLoading(false);
            },
        });
    };

    const onFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const reader = new FileReader();
        reader.onload = () => parseCsv(String(reader.result || ""));
        reader.onerror = () => setError("Could not read the file.");
        reader.readAsText(f);
    };

    // --- Load from Cloud (visible before/after data load) ---
    const loadFromCloud = async () => {
        if (!backEndUrl) {
            setError("No backEndUrl provided.");
            return;
        }
        const base = backEndUrl.replace(/\/+$/, "");
        setLoading(true);
        setError(null);
        try {
            const url = new URL(`${base}/deliverables`);
            url.searchParams.set("limit", "1000");
            if (userUuid) url.searchParams.set("uuid", userUuid);

            const res = await fetch(url.toString(), {
                method: "GET",
                headers: { Accept: "application/json" },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            const items = json?.items ?? [];
            loadItemsIntoState(items, "clean"); // Cloud -> clean
            flash(`Loaded ${items.length} ${items.length === 1 ? "row" : "rows"} from Cloud.`);
        } catch (err) {
            setError(`Failed to load from cloud: ${err.message || String(err)}`);
        } finally {
            setLoading(false);
        }
    };

    const toggleCol = (c) =>
        setVisibleCols((prev) =>
            prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]
        );

    const orderedVisible = headers.filter((h) => visibleCols.includes(h));

    const filteredSortedRows = useMemo(() => {
        // Add Exceptions computed field so sort/filter operate on its text
        let data = rows.map((r, idx) => ({
            __i: idx,
            ...r,
            [EXCEPTIONS_COL]: getExceptionsString(r),
        }));

        const conditions = Object.entries(filters)
            .map(([col, raw]) => ({ col, cond: parseFilter(raw) }))
            .filter(({ cond }) => !!cond);

        if (conditions.length) {
            data = data.filter((r) =>
                conditions.every(({ col, cond }) => {
                    // ✅ Special case: filter ADO Assignee by initials (e.g., "JD")
                    const rawVal = r[col] == null ? "" : String(r[col]);
                    const hay =
                        col === "System.AssignedTo.displayName"
                            ? getInitials(rawVal).toLowerCase()
                            : rawVal.toLowerCase();

                    return cond.mode === "not"
                        ? !hay.includes(cond.needle)
                        : hay.includes(cond.needle);
                })
            );
        }

        if (sortBy?.col && sortBy?.dir) {
            const col = sortBy.col;
            const dir = sortBy.dir === "asc" ? 1 : -1;

            if (col === "Rank") {
                data = [...data].sort((a, b) => {
                    const av = rankSortValue(a[col]);
                    const bv = rankSortValue(b[col]);
                    return dir * (av - bv);
                });
            } else {
                data = [...data].sort((a, b) => dir * cmp(a[col], b[col]));
            }
        }

        return data;
    }, [rows, filters, sortBy]);

    const onHeaderClick = (col) => {
        setSortBy((prev) => {
            if (!prev || prev.col !== col) return { col, dir: "asc" };
            const nextDir = sortCycle(prev.dir);
            return nextDir ? { col, dir: nextDir } : null;
        });
    };

    const sortIndicator = (col) =>
        sortBy?.col === col ? (sortBy.dir === "asc" ? " ▲" : " ▼") : "";

    const clearFilters = () => setFilters({});
    const resetSort = () => {
        if (headers.includes("Rank")) setSortBy({ col: "Rank", dir: "asc" });
        else setSortBy(null);
    };

    // --- Re-Rank action ---
    const reRank = () => {
        // Build a sortable copy with stable original index
        const data = rows.map((r, idx) => ({ ...r, __i: idx }));

        // Sort by: Driver, Go-live (date), Item Rank (num), Item
        const sorted = [...data].sort((a, b) => {
            const da = String(a.Driver ?? a["Driver"] ?? "").toLowerCase();
            const db = String(b.Driver ?? b["Driver"] ?? "").toLowerCase();
            if (da < db) return -1;
            if (da > db) return 1;

            const ga = parseDateOrInf(a["Go-live"]);
            const gb = parseDateOrInf(b["Go-live"]);
            if (ga !== gb) return ga - gb;

            const ira = itemRankSortValue(a["Item Rank"]);
            const irb = itemRankSortValue(b["Item Rank"]);
            if (ira !== irb) return ira - irb;

            const ia = String(a.Item ?? "").toLowerCase();
            const ib = String(b.Item ?? "").toLowerCase();
            if (ia < ib) return -1;
            if (ia > ib) return 1;
            return 0;
        });

        // Assign ranks (skip/blanks for drivers starting 97/98/99)
        let nextRank = 1;
        const newRankByIndex = new Map();
        for (const r of sorted) {
            const drv = r.Driver ?? r["Driver"] ?? "";
            if (startsWith979899(drv)) {
                newRankByIndex.set(r.__i, ""); // blank rank
            } else {
                newRankByIndex.set(r.__i, nextRank++);
            }
        }

        // Apply to state: update Rank and mark changed rows as needing save
        let changed = 0;
        setRows((prev) =>
            prev.map((r, i) => {
                const newRank = newRankByIndex.get(i);
                const oldRank = r.Rank ?? "";

                const oldRankNum = coerceRankNumber(oldRank);
                const isDifferent =
                    (newRank === "" && String(oldRank ?? "") !== "") ||
                    (newRank !== "" && Number(oldRankNum) !== Number(newRank));

                if (isDifferent) changed++;

                return isDifferent
                    ? {
                        ...r,
                        Rank: newRank,
                        __state:
                            r.__state === "new"
                                ? "new"
                                : r.__state === "deleted"
                                    ? "deleted"
                                    : "updated",
                    }
                    : r;
            })
        );

        flash(`Re-ranked items. ${changed} ${changed === 1 ? "row" : "rows"} changed.`);
    };

    const setScrums = () => {
        let changed = 0;

        const containsWord = (hay = "", needle = "") =>
            String(hay).toLowerCase().includes(String(needle).toLowerCase());
        const norm = (s) => String(s ?? "").trim().toLowerCase();

        setRows((prev) =>
            prev.map((r) => {
                if (r.__state === "deleted") return r;

                const type = norm(r["Type"]);
                const tshirt = norm(r["T-Shirt Size"]);
                const currentTeam = r["Team"] ?? "";
                const currentScrum = r["SCRUM"] ?? "";

                // 1) Report or Transcript -> AAIR / Reporting
                if (type === "report" || type === "transcript") {
                    const newTeam = "AAIR";
                    const newScrum = "Reporting";
                    if (currentTeam !== newTeam || currentScrum !== newScrum) {
                        changed++;
                        return { ...r, Team: newTeam, SCRUM: newScrum, __state: r.__state === "new" ? "new" : "updated" };
                    }
                    return r;
                }

                // 2) Data Conversion -> DS / Data
                if (type === "data conversion") {
                    const newTeam = "DS";
                    const newScrum = "Data";
                    if (currentTeam !== newTeam || currentScrum !== newScrum) {
                        changed++;
                        return { ...r, Team: newTeam, SCRUM: newScrum, __state: r.__state === "new" ? "new" : "updated" };
                    }
                    return r;
                }

                // 3) Product Gap Customization OR Discovery OR XL -> ERP / Arch
                if (type === "product gap customization" || type === "discovery" || tshirt === "xl") {
                    const newTeam = "ERP";
                    const newScrum = "Arch";
                    if (currentTeam !== newTeam || currentScrum !== newScrum) {
                        changed++;
                        return { ...r, Team: newTeam, SCRUM: newScrum, __state: r.__state === "new" ? "new" : "updated" };
                    }
                    return r;
                }

                // 4) Sherpa 2: Training / Consulting / contains "Hub" -> ERP / Sherpa 2
                const isSherpa2 =
                    type === "training" || type === "consulting" || containsWord(type, "hub");

                // 5) ERP Dev 2: (Workflow or Form) & T-Shirt Size != L  OR  contains "Package"
                const isWorkflowOrForm = type === "workflow" || type === "form";
                const tshirtIsNotL = tshirt !== "l"; // blank counts as not L
                const typeHasPackage = containsWord(type, "package");
                const toERPDev2 = (isWorkflowOrForm && tshirtIsNotL) || typeHasPackage || type === "bug fix";

                let newScrum = "ERP Dev 1"; // default
                if (isSherpa2) newScrum = "Sherpa 2";
                else if (toERPDev2) newScrum = "ERP Dev 2";

                // Force Team = ERP for ERP Dev 1/2 and Sherpa 2
                const needsErpTeam = ["erp dev 1", "erp dev 2", "sherpa 2"].includes(newScrum.toLowerCase());
                const newTeam = needsErpTeam ? "ERP" : currentTeam;

                if (currentScrum !== newScrum || currentTeam !== newTeam) {
                    changed++;
                    return {
                        ...r,
                        SCRUM: newScrum,
                        Team: newTeam,
                        __state:
                            r.__state === "new"
                                ? "new"
                                : r.__state === "deleted"
                                    ? "deleted"
                                    : "updated",
                    };
                }

                return r;
            })
        );

        flash(`Set SCRUMs complete. ${changed} ${changed === 1 ? "row" : "rows"} updated.`);
    };

    const setHours = () => {
        let changed = 0;

        const contains = (s, needle) => String(s ?? "").toLowerCase().includes(String(needle).toLowerCase());
        const norm = (s) => String(s ?? "").trim().toLowerCase();
        const isNotDone = (v) => norm(v) !== "done";
        const toNum = (v) => {
            const n = Number(String(v ?? "").replace(/[, ]/g, ""));
            return Number.isFinite(n) ? n : 0;
        };

        setRows((prev) =>
            prev.map((r) => {
                if (r.__state === "deleted") return r;

                const type = norm(r["Type"]);
                const tshirt = norm(r["T-Shirt Size"]);

                // Decide which rule applies (Package takes precedence over Form/Workflow)
                let rule = null; // 'package' | 'wf'
                if (contains(type, "package")) {
                    rule = "package";
                } else if ((type === "form" || type === "workflow") && tshirt !== "l") {
                    rule = "wf";
                }
                if (!rule) return r;

                // Current values
                const prevReq   = r["Reqs Hrs"];
                const prevArch  = r["Arch Hrs"];
                const prevDev   = r["Dev Hrs"];
                const prevQA    = r["Test Hrs"];
                const prevTotal = r["Total Hrs"];

                let nextReq  = prevReq;
                let nextArch = prevArch;
                let nextDev  = prevDev;
                let nextQA   = prevQA;

                if (rule === "package") {
                    if (isNotDone(r["Reqs Target"]))      nextReq  = 20;
                    if (isNotDone(r["Discovery Target"])) nextArch = 20;
                    if (isNotDone(r["Dev Target"]))       nextDev  = 20;
                    if (isNotDone(r["UAT Target"]))       nextQA   = 20;
                } else if (rule === "wf") {
                    if (isNotDone(r["Reqs Target"]))      nextReq  = 4;
                    if (isNotDone(r["Discovery Target"])) nextArch = 2;
                    if (isNotDone(r["Dev Target"]))       nextDev  = 8;
                    if (isNotDone(r["UAT Target"]))       nextQA   = 4;
                }

                // Recompute total from the four hour buckets
                const newTotal =
                    toNum(nextReq) + toNum(nextArch) + toNum(nextDev) + toNum(nextQA);

                const anyChange =
                    nextReq !== prevReq ||
                    nextArch !== prevArch ||
                    nextDev !== prevDev ||
                    nextQA !== prevQA ||
                    newTotal !== toNum(prevTotal);

                if (!anyChange) return r;

                changed++;
                return {
                    ...r,
                    "Reqs Hrs": nextReq,
                    "Arch Hrs": nextArch,
                    "Dev Hrs": nextDev,
                    "Test Hrs": nextQA,
                    "Total Hrs": newTotal,
                    __state: r.__state === "new" ? "new" : "updated",
                };
            })
        );

        flash(`Set Hours complete. ${changed} ${changed === 1 ? "row" : "rows"} updated.`);
    };

    // --- Form helpers ---
    const REQUIRED_FIELDS = ["Customer", "Item", "UAT Target", "Prod Target", "Item Status", "Release"];

    const openAddForm = () => {
        const empty = orderedFormFields.reduce((acc, h) => ((acc[h] = ""), acc), {});
        setFormData(empty);
        setFormMode("add");
        setEditIndex(null);
        setFormOpen(true); // ensure it opens at the top
    };

    const openEditForm = (rowIdx) => {
        const row = rows[rowIdx] || {};
        const filled = headers.reduce((acc, h) => ((acc[h] = row[h] ?? ""), acc), {});
        setFormData(filled);
        setFormMode("edit");
        setEditIndex(rowIdx);
        setFormOpen(true); // open inline under row
    };

    const submitForm = (e) => {
        e.preventDefault();
        // Coerce Rank to number before storing
        const coerced = {
            ...formData,
            ...(headers.includes("Rank")
                ? { Rank: coerceRankNumber(formData.Rank) ?? formData.Rank ?? "" }
                : {}),
        };

        if (formMode === "add") {
            const newRow = { ...coerced, __state: "new", __origItem: coerced.Item ?? "" };
            // Ensure audit fields exist (empty until saved)
            if (!(AUDIT_UPDATED in newRow)) newRow[AUDIT_UPDATED] = "";
            if (!(AUDIT_BY in newRow)) newRow[AUDIT_BY] = "";
            setRows((prev) => [...prev, newRow]);
            setFormOpen(false);
        } else if (formMode === "edit" && editIndex != null) {
            setRows((prev) =>
                prev.map((r, i) =>
                    i === editIndex
                        ? {
                            ...r,
                            ...coerced,
                            __state:
                                r.__state === "new"
                                    ? "new"
                                    : r.__state === "deleted"
                                        ? "deleted"
                                        : "updated",
                            // keep existing __origItem (original from load) to detect rename on save
                        }
                        : r
                )
            );
            setFormOpen(false);
            setEditIndex(null);
        }
    };

    // --- Row delete/undelete ---
    const toggleDelete = (rowIdx) => {
        setRows((prev) =>
            prev.map((r, i) => {
                if (i !== rowIdx) return r;
                const nextState =
                    r.__state === "deleted"
                        ? (r.__originalState ?? (r.__wasNew ? "new" : "clean"))
                        : "deleted";
                return {
                    ...r,
                    __originalState: r.__originalState ?? r.__state,
                    __wasNew: r.__state === "new" || r.__wasNew,
                    __state: nextState,
                };
            })
        );
    };

    // --- CSV Downloads (exclude meta since not in headers) ---
    const downloadCsv = (dataRows, fileLabel, fieldsOverride = null) => {
        if (!headers.length) return;

        // choose fields: visible or all (headers are already stripped of hidden fields)
        const fields = Array.isArray(fieldsOverride) && fieldsOverride.length
            ? fieldsOverride
            : headers;

        const csv = Papa.unparse({
            fields,
            data: dataRows.map((r) =>
                fields.map((h) =>
                    h === EXCEPTIONS_COL ? getExceptionsString(r) : (r[h] ?? "")
                )
            ),
        });
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `project-deliverables-${fileLabel}-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // Helper: remove client-only meta, keep domain fields
    function stripMeta(rec) {
        const { __state, __i, __originalState, __wasNew, __origItem, ...clean } = rec || {};
        HIDDEN_FIELDS.forEach((f) => delete clean[f]);
        return clean;
    }

    // Build PATCH/PUT payload for a given record (adds audit & updated_item if needed)
    const buildWritePayload = (rec) => {
        const base = stripMeta(rec);

        // Prepare audit stamp (computed at call-site to keep consistent timestamps)
        const payload = { ...base };

        // If the Item changed since load, include updated_item so backend performs a rename.
        const orig = rec.__origItem ?? "";
        const curr = rec.Item ?? "";
        if (rec.__state !== "new" && orig && curr && String(orig).trim() !== String(curr).trim()) {
            payload.updated_item = orig;
        }
        return payload;
    };

    // --- Save to Cloud: PATCH updated (404->PUT), PUT new, DELETE deleted ---
    const hasDirty = rows.some((r) => r.__state === "updated" || r.__state === "new" || r.__state === "deleted");

    const saveToCloud = async () => {
        if (!backEndUrl) {
            setError("No backEndUrl provided.");
            return;
        }
        const base = backEndUrl.replace(/\/+$/, "");
        const updated = rows.filter((r) => r.__state === "updated");
        const created = rows.filter((r) => r.__state === "new");
        const deleted = rows.filter((r) => r.__state === "deleted");

        // Prepare audit stamp (once per save call so timestamps are consistent)
        const stamp = {
            [AUDIT_UPDATED]: formatDateTimeLocal(new Date()),
            [AUDIT_BY]: userInitials || "—",
        };

        setLoading(true);
        setError(null);
        try {
            const endpointFor = () => {
                const u = new URL(`${base}/deliverables`);
                if (userUuid) u.searchParams.set("uuid", userUuid);
                return u.toString();
            };

            const doJSON = async (method, rec) => {
                const res = await fetch(endpointFor(), {
                    method,
                    headers: { Accept: "application/json", "Content-Type": "application/json" },
                    body: JSON.stringify(rec),
                });
                return res;
            };

            // For PATCH or PUT, always include audit fields before sending
            const withAudit = (rec) => ({ ...rec, ...stamp });

            const patchOrPut = async (payload) => {
                const patchRes = await doJSON("PATCH", withAudit(payload));
                if (patchRes.status === 404) {
                    const putRes = await doJSON("PUT", withAudit(payload));
                    if (!putRes.ok) throw new Error(`PUT ${putRes.status}`);
                    return "put";
                }
                if (!patchRes.ok) throw new Error(`PATCH ${patchRes.status}`);
                return "patch";
            };

            let updatedSaved = 0;
            let createdSaved = 0;
            let deletedSaved = 0;

            // Track which indices were saved to stamp UI afterwards
            const savedIdx = new Set();

            // UPDATED rows (may include rename via updated_item)
            for (let i = 0; i < updated.length; i++) {
                const r = updated[i];
                const payload = buildWritePayload(r);
                const mode = await patchOrPut(payload);
                if (mode === "patch") updatedSaved += 1;
                else createdSaved += 1; // 404->PUT
                savedIdx.add(rows.indexOf(r));
            }

            // NEW rows (never send updated_item)
            for (let i = 0; i < created.length; i++) {
                const r = created[i];
                const payload = stripMeta(r);
                const res = await doJSON("PUT", withAudit(payload));
                if (!res.ok) throw new Error(`PUT ${res.status}`);
                createdSaved += 1;
                savedIdx.add(rows.indexOf(r));
            }

            // DELETED rows
            for (let i = 0; i < deleted.length; i++) {
                const r = deleted[i];
                if (r.__wasNew) continue; // never persisted -> no DELETE
                const res = await doJSON("DELETE", stripMeta(r));
                if (!res.ok) throw new Error(`DELETE ${res.status}`);
                deletedSaved += 1;
            }

            // Update local rows: clear states; apply audit stamp to saved rows and sync __origItem after potential rename
            setRows((prev) =>
                prev
                    .filter((r) => r.__state !== "deleted")
                    .map((r, idx) => {
                        if (savedIdx.has(idx)) {
                            return {
                                ...r,
                                ...stamp,
                                __state: "clean",
                                __originalState: undefined,
                                __wasNew: undefined,
                                __origItem: r.Item ?? r.__origItem ?? "",
                            };
                        }
                        return {
                            ...r,
                            __state: r.__state === "deleted" ? "deleted" : "clean",
                            __originalState: undefined,
                            __wasNew: undefined,
                            __origItem: r.Item ?? r.__origItem ?? "",
                        };
                    })
            );

            const total = updatedSaved + createdSaved + deletedSaved;
            flash(
                `Saved ${total} ${total === 1 ? "row" : "rows"} ` +
                `(updated: ${updatedSaved}, created: ${createdSaved}, deleted: ${deletedSaved}).`
            );
        } catch (err) {
            setError(`Failed to save: ${err.message || String(err)}`);
        } finally {
            setLoading(false);
        }
    };

    const hasDataLoaded = headers.length > 0;

// --- 2025 Sprint table (inclusive date ranges) ---
    const SPRINT_RANGES_2025 = [
        { start: [2025,  1,  1], end: [2025,  1, 12], value: "2025 Sprint1"  },
        { start: [2025,  1, 13], end: [2025,  1, 26], value: "2025 Sprint2"  },
        { start: [2025,  1, 27], end: [2025,  2,  9], value: "2025 Sprint3"  },
        { start: [2025,  2, 10], end: [2025,  2, 23], value: "2025 Sprint4"  },
        { start: [2025,  2, 24], end: [2025,  3,  9], value: "2025 Sprint5"  },
        { start: [2025,  3, 10], end: [2025,  3, 23], value: "2025 Sprint6"  },
        { start: [2025,  3, 24], end: [2025,  4,  6], value: "2025 Sprint7"  },
        { start: [2025,  4,  7], end: [2025,  4, 20], value: "2025 Sprint8"  },
        { start: [2025,  4, 21], end: [2025,  5,   4], value: "2025 Sprint9"  },
        { start: [2025,  5,   5], end: [2025,  5,  18], value: "2025 Sprint10" },
        { start: [2025,  5,  19], end: [2025,  6,   1], value: "2025 Sprint11" },
        { start: [2025,  6,   2], end: [2025,  6,  15], value: "2025 Sprint12" },
        { start: [2025,  6,  16], end: [2025,  6,  29], value: "2025 Sprint13" },
        { start: [2025,  6,  30], end: [2025,  7,  13], value: "2025 Sprint14" },
        { start: [2025,  7,  14], end: [2025,  7,  27], value: "2025 Sprint15" },
        { start: [2025,  7,  28], end: [2025,  8,  10], value: "2025 Sprint16" },
        { start: [2025,  8,  11], end: [2025,  8,  24], value: "2025 Sprint17" },
        { start: [2025,  8,  25], end: [2025,  9,   7], value: "2025 Sprint18" },
        { start: [2025,  9,   8], end: [2025,  9,  21], value: "2025 Sprint19" },
        { start: [2025,  9,  22], end: [2025, 10,   5], value: "2025 Sprint20" },
        { start: [2025, 10,   6], end: [2025, 10,  19], value: "2025 Sprint21" },
        { start: [2025, 10,  20], end: [2025, 11,   2], value: "2025 Sprint22" },
        { start: [2025, 11,   3], end: [2025, 11,  16], value: "2025 Sprint23" },
        { start: [2025, 11,  17], end: [2025, 11,  30], value: "2025 Sprint24" },
        { start: [2025, 12,   1], end: [2025, 12,  14], value: "2025 Sprint25" },
        { start: [2025, 12,  15], end: [2025, 12,  28], value: "2025 Sprint26" },
    ];

    function ymd(dArr) { // [yyyy, m, d] with 1-based month
        return new Date(dArr[0], dArr[1]-1, dArr[2], 0, 0, 0, 0);
    }
    function isSameDayOrBetween(d, a, b) {
        const dd = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const aa = new Date(a.getFullYear(), a.getMonth(), a.getDate());
        const bb = new Date(b.getFullYear(), b.getMonth(), b.getDate());
        return dd >= aa && dd <= bb;
    }

    function currentSprintLabel(today = new Date()) {
        if (today.getFullYear() === 2025) {
            for (const r of SPRINT_RANGES_2025) {
                const s = ymd(r.start), e = ymd(r.end);
                if (isSameDayOrBetween(today, s, e)) return r.value;
            }
            // If outside the explicit windows, clamp to first/last
            if (today < ymd(SPRINT_RANGES_2025[0].start)) return SPRINT_RANGES_2025[0].value;
            return SPRINT_RANGES_2025[SPRINT_RANGES_2025.length - 1].value;
        }
        // Fallback for years without a table: default to YYYY Sprint1
        return `${today.getFullYear()} Sprint1`;
    }

    // ---------- ADO sync helpers ----------
    const fetchAdoWorkItem = async (id) => {
        if (!adoProxyUrl) throw new Error("No adoProxyUrl configured.");
        const sprintLabel = currentSprintLabel(new Date()); // e.g., "2025 Sprint19"
        const url = `${adoProxyUrl.replace(/\/+$/, "")}/workitems/${encodeURIComponent(id)}?sprint=${encodeURIComponent(sprintLabel)}`;
        const res = await fetch(url, { headers: { Accept: "application/json" } });
        if (!res.ok) throw new Error(`ADO ${res.status}`);
        return res.json();
    };

    const extractAdoFields = (wi) => {
        const f = wi?.fields || {};
        const assigned = f["System.AssignedTo"];
        const display =
            (assigned && (assigned.displayName || assigned.name || assigned.uniqueName)) ||
            (typeof assigned === "string" ? assigned : "") ||
            "";

        return {
            "System.AreaPath": f["System.AreaPath"] || "",
            "System.IterationPath": f["System.IterationPath"] || "",
            "System.AssignedTo.displayName": display,
            "System.Tags": Array.isArray(f["System.Tags"]) ? f["System.Tags"].join(", ") : (f["System.Tags"] || ""),
            "System.State": f["System.State"] || "",
            // ✅ new: map currentSprint -> Yes/No
            "Current Sprint": wi?.currentSprint === true ? "Yes" :
                wi?.currentSprint === false ? "No" : "",
        };
    };

    const ensureAdoColsInHeaders = () => {
        if (!headers.includes("ADO")) return;
        // Insert our three columns immediately after ADO in both headers and visibleCols
        setHeaders((prev) => insertColumnsNextTo(prev, "ADO", ADO_SYS_COLS));
        setVisibleCols((prev) => insertColumnsNextTo(prev, "ADO", ADO_SYS_COLS));
    };

    // Auto-sync ADO for all rows with ADO IDs (batch + cache)
    useEffect(() => {
        if (!adoProxyUrl || !rows.length) return;

        // Ensure columns are present and placed next to ADO
        if (headers.includes("ADO")) ensureAdoColsInHeaders();

        const ids = Array.from(
            new Set(
                rows
                    .map((r) => String(r.ADO ?? "").trim())
                    .filter(Boolean)
            )
        );

        // filter out already cached
        const toFetch = ids.filter((id) => !adoCache[id]);

        if (!toFetch.length) return;

        const BATCH = 6;
        let cancelled = false;

        const run = async () => {
            for (let i = 0; i < toFetch.length && !cancelled; i += BATCH) {
                const slice = toFetch.slice(i, i + BATCH);
                // mark busy
                setAdoBusyIds((s) => {
                    const n = new Set(s); slice.forEach((id) => n.add(id)); return n;
                });
                const results = await Promise.allSettled(
                    slice.map(async (id) => {
                        const wi = await fetchAdoWorkItem(id);
                        return { id, fields: extractAdoFields(wi) };
                    })
                );

                // update cache
                const cacheUpdates = {};
                results.forEach((res, idx) => {
                    const id = slice[idx];
                    if (res.status === "fulfilled") {
                        cacheUpdates[id] = res.value.fields;
                    } else {
                        cacheUpdates[id] = { // cache an empty object to avoid hot-looping failures
                            "System.AreaPath": "",
                            "System.IterationPath": "",
                            "System.AssignedTo.displayName": "",
                            "System.State": "",
                        };
                        setError(`ADO sync failed for ${id}: ${res.reason?.message || String(res.reason)}`);
                    }
                });
                setAdoCache((prev) => ({ ...prev, ...cacheUpdates }));

                // apply to rows (❗do NOT mark as updated — per request)
                setRows((prev) =>
                    prev.map((r) => {
                        const id = String(r.ADO ?? "").trim();
                        const f = cacheUpdates[id];
                        if (!id || !f) return r;
                        // Keep existing __state unchanged
                        return {
                            ...r,
                            ...f,
                        };
                    })
                );

                // clear busy marks
                setAdoBusyIds((s) => {
                    const n = new Set(s); slice.forEach((id) => n.delete(id)); return n;
                });
            }
            if (!cancelled && toFetch.length) {
                flash(`Synced ADO details for ${toFetch.length} row(s).`);
            }
        };

        run();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, adoProxyUrl]); // re-evaluates when new rows load

    // Map Item Status -> Driver & blank rank requirement
    const STATUS_TO_DRIVER = {
        "Cancelled": "98 - Cancelled",
        "On-Hold": "97 - On-Hold",
        "Completed": "99 - Completed",
    };

    /** ---------- Target/Date helpers used in the form ---------- **/
        // Convert stored "MM/DD/YY" or "MM/DD/YYYY" or "YYYY-MM-DD" -> "YYYY-MM-DD" for <input type="date">
        // For "DONE" or blank, return "" so the date input is clear.
    const targetToDateInputValue = (v) => {
            if (!v) return "";
            const s = String(v).trim();
            if (s.toUpperCase() === "DONE") return "";
            const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
            if (mdy) {
                let [, mm, dd, yy] = mdy;
                if (yy.length === 2) yy = "20" + yy; // assume 20xx
                return `${yy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
            }
            const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            if (ymd) return s;
            return "";
        };

    // Convert "YYYY-MM-DD" from the date input -> "MM/DD/YY" for storage
    const dateInputToStoredMdy = (val) => {
        if (!val) return "";
        const [yyyy, mm, dd] = val.split("-");
        const yy = String(yyyy).slice(-2);
        return `${mm}/${dd}/${yy}`;
    };

    // Reusable renderer for any Target field (stores MM/DD/YY or "DONE")
    const renderTargetControl = (h, value, isRequired, setFormData) => {
        const isDone = String(value ?? "").trim().toUpperCase() === "DONE";
        return (
            <label key={h} style={{ display: "grid", gap: 4 }}>
                <span style={{ fontSize: 12, color: "var(--anth-muted)" }}>
                    {h}{isRequired && " *"}
                </span>

                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    {isDone ? (
                        <>
                            <input
                                type="text"
                                value="DONE"
                                readOnly
                                style={{
                                    flex: "0 0 140px",
                                    textAlign: "center",
                                    fontWeight: 600,
                                    background: "#f0f0f0",
                                }}
                            />
                            <button
                                type="button"
                                className="bb-button"
                                style={{ padding: "2px 6px", fontSize: 12 }}
                                onClick={() => setFormData((fd) => ({ ...fd, [h]: "" }))}
                                title="Switch back to a date"
                            >
                                Set Date
                            </button>
                        </>
                    ) : (
                        <>
                            <input
                                type="date"
                                value={targetToDateInputValue(value)}
                                onChange={(e) =>
                                    setFormData((fd) => ({
                                        ...fd,
                                        [h]: e.target.value ? dateInputToStoredMdy(e.target.value) : "",
                                    }))
                                }
                                required={isRequired}
                                style={{ flex: "0 0 180px" }}
                            />
                            <button
                                type="button"
                                className="bb-button"
                                style={{ padding: "2px 6px", fontSize: 12 }}
                                onClick={() => setFormData((fd) => ({ ...fd, [h]: "DONE" }))}
                                title='Mark as "DONE"'
                            >
                                Mark DONE
                            </button>
                        </>
                    )}
                </div>
            </label>
        );
    };

    const renderForm = () => {
        // Dynamic requirement rule for Training/Consulting:
        const typeValue = String(formData["Type"] ?? "").trim().toLowerCase();
        const trainingOrConsulting = typeValue === "training" || typeValue === "consulting";

        return (
            <div className="bb-card" style={{ marginBottom: 16, padding: 16 }}>
                <h2 className="bb-title" style={{ fontSize: 18, marginBottom: 12 }}>
                    {formMode === "add" ? "Add Row" : "Update Row"}
                </h2>
                <form onSubmit={submitForm}>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                            gap: 12,
                            marginBottom: 12,
                        }}
                    >
                        {orderedFormFields.map((h) => {
                            // Base required rule
                            let isRequired = REQUIRED_FIELDS.includes(h);
                            // Override for Training/Consulting: UAT/Prod not required; C/T required
                            if (trainingOrConsulting) {
                                if (h === "UAT Target" || h === "Prod Target") isRequired = false;
                                if (h === "C/T Target") isRequired = true;
                            }
                            // Final requirement: Admin bypass
                            const effectiveRequired = isRequired && role !== "ADMIN";

                            // Any Target field gets the date picker + small "Mark DONE" toggle
                            if (DEADLINE_FIELDS.includes(h)) {
                                return renderTargetControl(h, formData[h], effectiveRequired, setFormData);
                            }

                            // Item Status dropdown
                            if (h === "Item Status") {
                                return (
                                    <label key={h} style={{ display: "grid", gap: 4 }}>
                                        <span style={{ fontSize: 12, color: "var(--anth-muted)" }}>
                                          {h}{effectiveRequired && " *"}
                                        </span>
                                        <select
                                            value={formData[h] ?? ""}
                                            onChange={(e) => {
                                                const newStatus = e.target.value;
                                                setFormData((fd) => {
                                                    const next = { ...fd, [h]: newStatus };
                                                    // Map certain statuses -> Driver and blank Rank
                                                    if (STATUS_TO_DRIVER[newStatus]) {
                                                        next["Rank"] = "";
                                                        next["Driver"] = STATUS_TO_DRIVER[newStatus];
                                                    }
                                                    return next;
                                                });
                                            }}
                                            required={effectiveRequired}
                                        >
                                            <option value="" disabled>Select status…</option>
                                            <option value="Active">Active</option>
                                            <option value="On-Hold">On-Hold</option>
                                            <option value="Cancelled">Cancelled</option>
                                            <option value="Completed">Completed</option>
                                        </select>
                                    </label>
                                );
                            }

                            // T-Shirt Size dropdown (blank, XL, L, M, S)
                            if (h === "T-Shirt Size") {
                                return (
                                    <label key={h} style={{ display: "grid", gap: 4 }}>
                                        <span style={{ fontSize: 12, color: "var(--anth-muted)" }}>
                                          {h}{effectiveRequired && " *"}
                                        </span>
                                        <select
                                            value={formData[h] ?? ""}
                                            onChange={(e) =>
                                                setFormData((fd) => ({ ...fd, [h]: e.target.value }))
                                            }
                                            required={effectiveRequired}
                                        >
                                            <option value="">—</option>
                                            <option value="XL">XL</option>
                                            <option value="L">L</option>
                                            <option value="M">M</option>
                                            <option value="S">S</option>
                                        </select>
                                    </label>
                                );
                            }

                            // Type dropdown (curated list, minus Hub Payment Integration)
                            if (h === "Type") {
                                const typeOptions = [
                                    "", // blank
                                    "Account Automation",
                                    "Bug Fix",
                                    "Bookstore Vendor Integration Package",
                                    "Consulting",
                                    "Custom Integration",
                                    "Data Conversion",
                                    "Discovery",
                                    "Emergency Alert System Integration Package",
                                    "External Payment Service Vendor Integration Package",
                                    "Form",
                                    "Form/Workflow",
                                    "General Ledger Exports Vendor Integration Package",
                                    "LMS Vendor Integration Package",
                                    "Portal Customization",
                                    "Portal Report",
                                    "Product Gap Customization",
                                    "Product Integration",
                                    "Product Integration (Hub)",
                                    "QA Automation",
                                    "Reach Customization",
                                    "Reach Integration",
                                    "Report",
                                    "Room Scheduling Vendor Integration Package",
                                    "Training",
                                    "Transcript",
                                    "Transcript Vendor Integration Package",
                                    "Unknown",
                                    "Workflow",
                                ];
                                return (
                                    <label key={h} style={{ display: "grid", gap: 4 }}>
                                        <span style={{ fontSize: 12, color: "var(--anth-muted)" }}>
                                          {h}{effectiveRequired && " *"}
                                        </span>
                                        <select
                                            value={formData[h] ?? ""}
                                            onChange={(e) =>
                                                setFormData((fd) => ({ ...fd, [h]: e.target.value }))
                                            }
                                            required={effectiveRequired}
                                        >
                                            {typeOptions.map((opt, i) => (
                                                <option key={i} value={opt}>
                                                    {opt || "—"}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                );
                            }

                            // Team dropdown (blank, Bb, ERP, DS, AAIR, PS Support)
                            if (h === "Team") {
                                const teamOptions = ["", "Bb", "ERP", "DS", "AAIR", "PS Support"];
                                return (
                                    <label key={h} style={{ display: "grid", gap: 4 }}>
                                        <span style={{ fontSize: 12, color: "var(--anth-muted)" }}>
                                          {h}{effectiveRequired && " *"}
                                        </span>
                                        <select
                                            value={formData[h] ?? ""}
                                            onChange={(e) =>
                                                setFormData((fd) => ({ ...fd, [h]: e.target.value }))
                                            }
                                            required={effectiveRequired}
                                        >
                                            {teamOptions.map((opt, i) => (
                                                <option key={i} value={opt}>
                                                    {opt || "—"}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                );
                            }

                            // SCRUM dropdown (blank, ERP Dev 1/2, Sherpa 2, Arch, Reporting, Data)
                            if (h === "SCRUM") {
                                const scrumOptions = ["", "ERP Dev 1", "ERP Dev 2", "Sherpa 2", "Arch", "Reporting", "Data"];
                                return (
                                    <label key={h} style={{ display: "grid", gap: 4 }}>
                                        <span style={{ fontSize: 12, color: "var(--anth-muted)" }}>
                                          {h}{effectiveRequired && " *"}
                                        </span>
                                        <select
                                            value={formData[h] ?? ""}
                                            onChange={(e) =>
                                                setFormData((fd) => ({ ...fd, [h]: e.target.value }))
                                            }
                                            required={effectiveRequired}
                                        >
                                            {scrumOptions.map((opt, i) => (
                                                <option key={i} value={opt}>
                                                    {opt || "—"}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                );
                            }

                            // Driver dropdown (blank + provided values; "97 - On Hold" removed)
                            if (h === "Driver") {
                                const driverOptions = [
                                    "",
                                    "1 - Supports Critical Escalation",
                                    "2 - Go-live Work Highest Priority",
                                    "3 - Ad Hoc Request",
                                    "4 - High Priority Work for Past Go-Live",
                                    "5 - Go-live Work Regular Priority",
                                    "97 - On-Hold",
                                    "98 - Cancelled",
                                    "99 - Completed",
                                ];
                                return (
                                    <label key={h} style={{ display: "grid", gap: 4 }}>
                                        <span style={{ fontSize: 12, color: "var(--anth-muted)" }}>
                                          {h}{effectiveRequired && " *"}
                                        </span>
                                        <select
                                            value={formData[h] ?? ""}
                                            onChange={(e) =>
                                                setFormData((fd) => ({ ...fd, [h]: e.target.value }))
                                            }
                                            required={effectiveRequired}
                                        >
                                            {driverOptions.map((opt, i) => (
                                                <option key={i} value={opt}>
                                                    {opt || "—"}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                );
                            }

                            // Urgency dropdown (blank, Critical, High, Low)
                            if (h === "Urgency") {
                                const urgencyOptions = ["", "Critical", "High", "Low", "Not Needed"];
                                return (
                                    <label key={h} style={{ display: "grid", gap: 4 }}>
                                        <span style={{ fontSize: 12, color: "var(--anth-muted)" }}>
                                          {h}{effectiveRequired && " *"}
                                        </span>
                                        <select
                                            value={formData[h] ?? ""}
                                            onChange={(e) =>
                                                setFormData((fd) => ({ ...fd, [h]: e.target.value }))
                                            }
                                            required={effectiveRequired}
                                        >
                                            {urgencyOptions.map((opt, i) => (
                                                <option key={i} value={opt}>
                                                    {opt || "—"}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                );
                            }

                            // Go-live date picker (store as MM/DD/YY) — pure string conversion to avoid timezone issues
                            if (h === "Go-live") {
                                return (
                                    <label key={h} style={{ display: "grid", gap: 4 }}>
                                        <span style={{ fontSize: 12, color: "var(--anth-muted)" }}>
                                          {h}{effectiveRequired && " *"}
                                        </span>
                                        <input
                                            type="date"
                                            value={targetToDateInputValue(formData[h])}
                                            onChange={(e) =>
                                                setFormData((fd) => ({
                                                    ...fd,
                                                    [h]: e.target.value ? dateInputToStoredMdy(e.target.value) : "",
                                                }))
                                            }
                                            required={effectiveRequired}
                                        />
                                    </label>
                                );
                            }

                            // Default text input
                            return (
                                <label key={h} style={{ display: "grid", gap: 4 }}>
                                    <span style={{ fontSize: 12, color: "var(--anth-muted)" }}>
                                        {h}{effectiveRequired && " *"}
                                    </span>
                                    <input
                                        type="text"
                                        value={formData[h] ?? ""}
                                        onChange={(e) =>
                                            setFormData((fd) => ({ ...fd, [h]: e.target.value }))
                                        }
                                        required={effectiveRequired}
                                    />
                                </label>
                            );
                        })}
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                        <button className="bb-button" type="submit">
                            {formMode === "add" ? "Add" : "Update"}
                        </button>
                        <button
                            className="bb-button"
                            type="button"
                            onClick={() => {
                                setFormOpen(false);
                                setEditIndex(null);
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        );
    };

    return (
        <div className="bb-container" style={{ position: "relative" }}>
            {/* Help link in upper right */}
            <a
                href="https://platform-extensions-int.blackboard.com/bbcswebdav/xid-273015_1"
                target="_blank"
                rel="noopener noreferrer"
                className="bb-help-link"
                style={{ position: "absolute", top: 10, right: 10 }}
            >
                Help
            </a>

            {/* Welcome banner using Blackboard user info */}
            {userInfo?.name && (
                <div style={{ marginBottom: 8, fontSize: 16, fontWeight: 500 }}>
                    Welcome {userInfo.name?.given ?? ""} {userInfo.name?.family ?? ""}
                    {role && <p>Your role is {role}</p>}
                </div>
            )}

            <h1 className="bb-title">Technical Innovations Portfolio Deliverables</h1>

            {/* Primary load controls */}
            <div className="bb-controls" style={{ marginBottom: 8 }}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 12,
                        justifyContent: "space-between",
                    }}
                >
                    {/* LEFT: Load / Save */}
                    <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                        <button
                            className="bb-button"
                            onClick={loadFromCloud}
                            disabled={!backEndUrl || loading}
                            title={backEndUrl ? "Load from Cloud" : "Provide backEndUrl to enable"}
                        >
                            {loading ? "Loading…" : "Load from Cloud"}
                        </button>

                        {hasDataLoaded && (
                            <button
                                className="bb-button"
                                onClick={saveToCloud}
                                disabled={!backEndUrl || loading || !hasDirty}
                                title={
                                    !backEndUrl
                                        ? "Provide backEndUrl to enable"
                                        : hasDirty
                                            ? "Save changes (PATCH/PUT/DELETE)"
                                            : "No changes to save"
                                }
                            >
                                {loading ? "Saving…" : "Save to Cloud"}
                            </button>
                        )}
                    </div>

                    {/* RIGHT: Upload CSV */}
                    <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        <span>Upload CSV:</span>
                        <input type="file" accept=".csv,text/csv" onChange={onFile} disabled={loading} />
                    </label>
                </div>
            </div>

            {/* Messages */}
            {(info || error) && (
                <div style={{ marginBottom: 12 }}>
                    {info && (
                        <div className="bb-info" style={{ padding: 8 }}>
                            {info}
                        </div>
                    )}
                    {error && (
                        <div className="bb-error" style={{ padding: 8, marginTop: 8 }}>
                            <strong>Error:</strong> {error}
                        </div>
                    )}
                </div>
            )}

            {/* Sprint Calendar strip */}
            <div
                className="bb-card"
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    padding: 10,
                    marginBottom: 10,
                }}
            >
                <div style={{ fontWeight: 600 }}>Sprint Calendar</div>
                <button className="bb-button" onClick={() => setShowSprint((s) => !s)}>
                    {showSprint ? "Hide" : "Show"}
                </button>
            </div>

            {showSprint && (
                <div
                    className="bb-card"
                    style={{
                        padding: 8,
                        marginBottom: 12,
                        overflow: "hidden",
                        border: "1px solid rgba(0,0,0,0.06)",
                        display: "flex",
                        justifyContent: "center",
                    }}
                >
                    <img
                        src={sprintCalendarUrl}
                        alt="Sprint Calendar"
                        style={{
                            width: "100%",
                            maxWidth: "1200px",
                            height: "auto",
                            objectFit: "contain",
                            display: "block",
                        }}
                    />
                </div>
            )}

            {loading && <div>Working…</div>}

            {hasDataLoaded ? (
                <>
                    <div className="bb-legend" style={{ marginBottom: 8 }}>
                        <span style={{ marginRight: 12 }}>Legend:</span>
                        <span style={{ marginRight: 12 }}>🆕 New</span>
                        <span style={{ marginRight: 12 }}>✱ Updated</span>
                        <span>🗑️ Deleted</span>
                    </div>

                    {/* Action buttons + include-only checkbox */}
                    <div style={{display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 12}}>
                        <button className="bb-button" onClick={() => openAddForm()}>
                            {formOpen && formMode === "add" ? "Close Form" : "Add Item"}
                        </button>

                        <button className="bb-button" onClick={reRank}>Re-Rank</button>
                        <button className="bb-button" onClick={setScrums}>Set SCRUMs</button>
                        <button className="bb-button" onClick={setHours}>Set Hours</button>

                        {/* CSV + utilities */}
                        <button
                            className="bb-button"
                            onClick={() =>
                                downloadCsv(
                                    rows,
                                    "all",
                                    includeOnlyDisplayed && orderedVisible.length ? orderedVisible : null
                                )
                            }
                        >
                            Download CSV (All)
                        </button>
                        <button
                            className="bb-button"
                            onClick={() =>
                                downloadCsv(
                                    filteredSortedRows,
                                    "filtered",
                                    includeOnlyDisplayed && orderedVisible.length ? orderedVisible : null
                                )
                            }
                        >
                            Download CSV (Filtered)
                        </button>
                        <button className="bb-button" onClick={resetSort}>Reset Sort</button>
                        <button className="bb-button" onClick={clearFilters}>Clear Filters</button>

                        {/* Include Only Displayed Fields */}
                        <div style={{display: "inline-flex", alignItems: "center", gap: 6}}>
                            <input
                                id="includeOnlyDisplayed"
                                type="checkbox"
                                checked={includeOnlyDisplayed}
                                onChange={(e) => setIncludeOnlyDisplayed(e.target.checked)}
                                style={{width: 16, height: 16, margin: 0}}
                            />
                            <label htmlFor="includeOnlyDisplayed" style={{userSelect: "none", cursor: "pointer"}}>
                                Include Only Displayed Fields
                            </label>
                        </div>
                    </div>

                    {/* Top-level Add form only */}
                    {formOpen && formMode === "add" && renderForm()}

                    <div style={{ marginBottom: 16 }}>
                        <div style={{ fontWeight: 600, marginBottom: 8 }}>Select columns to display:</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                            {headers.map((h) => {
                                const isVisible = visibleCols.includes(h);
                                return (
                                    <label key={h} style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                                        <input
                                            type="checkbox"
                                            checked={isVisible}
                                            onChange={() => toggleCol(h)}
                                        />
                                        <span style={{ fontWeight: isVisible ? 700 : 400 }}>
                                            {displayHeader(h)}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    {orderedVisible.length > 0 && (
                        <table className="bb-table">
                            <thead>
                            <tr>
                                <th>Actions</th>
                                {orderedVisible.map((h) => (
                                    <th
                                        key={h}
                                        style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                                        onClick={() => onHeaderClick(h)}
                                        title="Click to sort"
                                    >
                                        {displayHeader(h)}{sortIndicator(h)}
                                    </th>
                                ))}
                            </tr>
                            <tr>
                                <th />
                                {orderedVisible.map((h) => (
                                    <th key={h}>
                                        <input
                                            type="text"
                                            value={filters[h] ?? ""}
                                            onChange={(e) => setFilters((f) => ({ ...f, [h]: e.target.value }))}
                                            placeholder="filter… (use !term or NOT term)"
                                            style={{ width: "100%" }}
                                        />
                                    </th>
                                ))}
                            </tr>
                            </thead>
                            <tbody>
                            {filteredSortedRows.length === 0 ? (
                                <tr>
                                    <td colSpan={orderedVisible.length + 1} style={{ textAlign: "center", color: "var(--anth-muted)" }}>
                                        No rows match your filters.
                                    </td>
                                </tr>
                            ) : (
                                filteredSortedRows.flatMap((r) => {
                                    const rowEls = [];
                                    const isDeleted = r.__state === "deleted";
                                    const isUpdated = r.__state === "updated";
                                    const isNew = r.__state === "new";

                                    rowEls.push(
                                        <tr
                                            key={r.__i}
                                            style={{
                                                background:
                                                    isDeleted ? "rgba(128,128,128,0.08)"
                                                        : isNew ? "rgba(0,200,0,0.06)"
                                                            : isUpdated ? "rgba(255,165,0,0.08)"
                                                                : undefined,
                                                textDecoration: isDeleted ? "line-through" : undefined,
                                                opacity: isDeleted ? 0.7 : 1,
                                            }}
                                        >
                                            <td>
                                                <a
                                                    href="#edit"
                                                    onClick={(e) => { e.preventDefault(); if (!isDeleted) openEditForm(r.__i); }}
                                                    style={{ color: isDeleted ? "#aaa" : undefined, pointerEvents: isDeleted ? "none" : "auto" }}
                                                    title={isDeleted ? "Undo delete to edit" : "Edit"}
                                                >
                                                    Edit
                                                </a>
                                                {" · "}
                                                <a
                                                    href="#delete"
                                                    onClick={(e) => { e.preventDefault(); toggleDelete(r.__i); }}
                                                    title={isDeleted ? "Undo delete" : "Mark for delete"}
                                                >
                                                    {isDeleted ? "Undo" : "Delete"}
                                                </a>
                                                {" "}
                                                {isNew && <span title="New">🆕</span>}
                                                {isUpdated && <span title="Updated">✱</span>}
                                                {isDeleted && <span title="Deleted">🗑️</span>}
                                            </td>
                                            {orderedVisible.map((h) => (
                                                <td key={h}>{renderCell(h, r[h], r)}</td>
                                            ))}
                                        </tr>
                                    );

                                    // Inline edit form row appears directly below the edited record
                                    if (formOpen && formMode === "edit" && editIndex === r.__i) {
                                        rowEls.push(
                                            <tr key={`edit-form-${r.__i}`}>
                                                <td colSpan={orderedVisible.length + 1}>
                                                    {renderForm()}
                                                </td>
                                            </tr>
                                        );
                                    }

                                    return rowEls;
                                })
                            )}
                            </tbody>
                        </table>
                    )}
                </>
            ) : (
                !loading && (
                    <div style={{ color: "var(--anth-muted)", marginTop: 8 }}>
                        Upload a CSV <em>or</em> click <strong>Load from Cloud</strong> to display the table.
                    </div>
                )
            )}
        </div>
    );
};

// Render helpers
const renderCell = (col, value, row = {}) => {
    // Exceptions column: show icon + text; sortable/filterable via text value
    if (col === EXCEPTIONS_COL) {
        const text = getExceptionsString(row);
        if (!text) return "";
        return (
            <span title={text} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span aria-label="Exception" style={{ cursor: "help" }}>⚠️</span>
                <span>{text}</span>
            </span>
        );
    }

    // Date highlighting for target fields
    if (DEADLINE_FIELDS.includes(col)) {
        const { style, title } = getDateHighlight(value);
        const content = value ?? "";
        if (style) {
            return (
                <span style={style} title={title || undefined}>
                    {content}
                </span>
            );
        }
        return content;
    }

    if (col === "ADO") {
        const id = String(value ?? "").trim();

        // ✅ Case 1: ADO populated → Azure DevOps link
        if (id && id.toLowerCase() !== "n/a") {
            const href = `https://dev.azure.com/campusmgmt-ps/INTS/_workitems/edit/${encodeURIComponent(id)}`;
            return (
                <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`Open ADO work item ${id}`}
                >
                    {id}
                </a>
            );
        }

        // ✅ Case 2: ADO blank AND Team is AAIR → SharePoint link
        if ((!id || id.toLowerCase() === "n/a") && row.Team === "AAIR") {
            const sharepointUrl =
                "https://anthologyinc.sharepoint.com/:x:/r/sites/analyticsprofessionalservicesteam/Shared%20Documents/Custom%20Reporting/FY26%20Report%20Development%20Status.xlsx?web=1";
            return (
                <a
                    href={sharepointUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open FY26 Report Development Status"
                    style={{ color: "var(--anth-link, blue)", textDecoration: "underline" }}
                >
                    Report Status
                </a>
            );
        }

        // ✅ Case 3: ADO blank AND Team is DS → SharePoint link
        if ((!id || id.toLowerCase() === "n/a") && row.Team === "DS") {
            const sharepointUrl =
                "https://anthologyinc-my.sharepoint.com/:x:/g/personal/alexandria_mercado_anthology_com/EWLO8Ocp1ttBjTlQWBhiEzcBQHkwBV_oXxD2HzsSL-TBsA?e=jzMqd9&wdLOR=c65C1920D-7C25-E942-8898-DA222A73AEE6";
            return (
                <a
                    href={sharepointUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open Data Migration Status"
                    style={{ color: "var(--anth-link, blue)", textDecoration: "underline" }}
                >
                    Data Status
                </a>
            );
        }

        return "";
    }

    if (col === "System.AssignedTo.displayName") {
        // Show initials instead of full display name, and label is handled in header with displayHeader()
        return getInitials(value);
    }

    if (col === "Tracker") {
        const url = String(value ?? "").trim();
        if (!url) return "";
        return (
            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                title={`Open Tracker link`}
                style={{ color: "var(--anth-link, blue)", textDecoration: "underline" }}
            >
                Tracker
            </a>
        );
    }

    if (col === "SOW") {
        const url = String(value ?? "").trim();
        if (!url) return "";
        return (
            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                title={`Open SOW link`}
                style={{ color: "var(--anth-link, blue)", textDecoration: "underline" }}
            >
                SOW
            </a>
        );
    }

    if (col === "System.Tags") {
        return value ?? "";
    }

    return value ?? "";
};

export default ProjectDeliverablesTable;
